/**
 * Section.js
 *
 * Componente CraftJS para elementos HTML semánticos:
 * <header>, <footer>, <section>, <article>, <aside>, <nav>, <main>
 *
 * Al publicar en producción, el Lambda renderer emitirá el tagName
 * correcto en lugar de un <div> genérico, preservando el SEO y la
 * semántica del HTML original.
 *
 * rawStyle: objeto de estilos CSS computados heredados del HTML importado.
 * Si el usuario edita las props del componente (padding, backgroundColor, etc.),
 * esas props tienen prioridad sobre rawStyle.
 */
import React from 'react';
import { useNode } from '@craftjs/core';
import {
  TextField, FormControl, FormLabel, Typography, Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  section: {
    width: '100%',
    minHeight: '60px',
    cursor: 'pointer',
    outline: '1px dashed transparent',
    transition: 'outline-color 0.15s',
    '&:hover': {
      outlineColor: '#2196f3',
    },
  },
  settings: {
    padding: theme.spacing(2),
  },
  rawStyleTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
    padding: theme.spacing(1),
    border: '1px solid #ccc',
    borderRadius: 4,
    minHeight: 120,
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#fafafa',
    '&:focus': {
      borderColor: '#2196f3',
    },
  },
  hint: {
    fontSize: '0.72rem',
    color: '#999',
    marginTop: 4,
    marginBottom: theme.spacing(1),
  },
  tagBadge: {
    display: 'inline-block',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    borderRadius: 3,
    padding: '2px 8px',
    fontSize: '0.72rem',
    fontFamily: 'monospace',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
}));

// Etiquetas semánticas soportadas
const SEMANTIC_TAGS = ['header', 'footer', 'section', 'article', 'aside', 'nav', 'main', 'div'];

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────

export const Section = ({
  children,
  tagName,
  backgroundColor,
  padding,
  flexDirection,
  rawStyle,
}) => {
  const classes = useStyles();
  const {
    connectors: { connect, drag },
  } = useNode();

  // Combinar estilos: rawStyle base + props del editor encima
  const computedStyle = {
    ...rawStyle,
    ...(backgroundColor && backgroundColor !== 'transparent'
      ? { backgroundColor }
      : {}),
    ...(padding ? { padding: `${padding}px` } : {}),
    display: rawStyle?.display || 'flex',
    flexDirection: flexDirection || rawStyle?.flexDirection || 'column',
    width: '100%',
  };

  const Tag = SEMANTIC_TAGS.includes(tagName) ? tagName : 'div';

  return (
    <Tag
      ref={(ref) => connect(drag(ref))}
      className={classes.section}
      style={computedStyle}
    >
      {children}
    </Tag>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────

const SectionSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    tagName,
    backgroundColor,
    padding,
    flexDirection,
    rawStyle,
  } = useNode((node) => ({
    tagName:         node.data.props.tagName,
    backgroundColor: node.data.props.backgroundColor,
    padding:         node.data.props.padding,
    flexDirection:   node.data.props.flexDirection,
    rawStyle:        node.data.props.rawStyle,
  }));

  const rawStyleStr = rawStyle ? JSON.stringify(rawStyle, null, 2) : '{}';

  const handleRawStyleChange = (val) => {
    try {
      const parsed = JSON.parse(val);
      setProp((props) => (props.rawStyle = parsed));
    } catch (_) {
      // JSON inválido → no actualizamos (el usuario sigue escribiendo)
    }
  };

  return (
    <div className={classes.settings}>

      {/* Badge semántico */}
      {tagName && (
        <Box mb={1}>
          <span className={classes.tagBadge}>&lt;{tagName}&gt;</span>
        </Box>
      )}

      {/* Etiqueta HTML */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Etiqueta HTML</FormLabel>
        <TextField
          select
          value={tagName || 'section'}
          onChange={(e) => setProp((props) => (props.tagName = e.target.value))}
          SelectProps={{ native: true }}
        >
          {SEMANTIC_TAGS.map((t) => (
            <option key={t} value={t}>{`<${t}>`}</option>
          ))}
        </TextField>
      </FormControl>

      {/* Color de fondo */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Color de fondo</FormLabel>
        <TextField
          type="color"
          value={backgroundColor && backgroundColor !== 'transparent' ? backgroundColor : '#ffffff'}
          onChange={(e) => setProp((props) => (props.backgroundColor = e.target.value))}
        />
      </FormControl>

      {/* Padding */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Padding (px)</FormLabel>
        <TextField
          type="number"
          value={padding || 0}
          onChange={(e) => setProp((props) => (props.padding = Number(e.target.value)))}
        />
      </FormControl>

      {/* Dirección flex */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Dirección</FormLabel>
        <TextField
          select
          value={flexDirection || 'column'}
          onChange={(e) => setProp((props) => (props.flexDirection = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="column">Vertical (column)</option>
          <option value="row">Horizontal (row)</option>
          <option value="row-reverse">Horizontal invertido</option>
          <option value="column-reverse">Vertical invertido</option>
        </TextField>
      </FormControl>

      {/* rawStyle - estilos CSS computados del HTML original */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Estilos heredados (CSS)</FormLabel>
        <Typography className={classes.hint}>
          Estilos capturados del HTML original. Edita como JSON.
          Las props de arriba tienen prioridad sobre estos.
        </Typography>
        <textarea
          className={classes.rawStyleTextarea}
          defaultValue={rawStyleStr}
          onBlur={(e) => handleRawStyleChange(e.target.value)}
          spellCheck={false}
        />
      </FormControl>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Craft config
// ─────────────────────────────────────────────────────────────────────────────

Section.craft = {
  props: {
    tagName:         'section',
    backgroundColor: 'transparent',
    padding:         0,
    flexDirection:   'column',
    rawStyle:        {},
  },
  related: {
    settings: SectionSettings,
  },
};
